import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { createTenantUser } from "../../../services/TenantsService";
import { toast } from "react-toastify";
import Select from "react-select";
import { DatePicker } from "rsuite";
import {
  createAndUpdateCoach,
  createAndUpdateTournaments,
  getBallType,
  getCoachSkill,
  getMatchType,
} from "../../../services/academicService";
import { formatDate } from "../../../utils/helperFunction";

const AddTournaments = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const editUser = location.state?.user || null;
  const mode = location.state?.mode || "add";
  const isEditMode = mode === "edit" && !!editUser;

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [matchType, setMatchType] = useState([]);
  const [ballType, setBallType] = useState([]);
  const [selectedMatchType, setSelectedMatchType] = useState(null);
  const [selectedBallType, setSelectedBallType] = useState(null);

  const [formData, setFormData] = useState({
    name: "",
    startDate: null,
    endDate: null,
    overs: "",
    reportingTime: "",
    groundLocation: "",
    opponents: "",
    matchFee: "",
    remarks: "",
  });

  useEffect(() => {
    if (isEditMode && editUser) {
      setFormData({
        name: editUser.name || "",
        startDate: editUser.startDate ? new Date(editUser.startDate) : null,
        endDate: editUser.endDate ? new Date(editUser.endDate) : null,
        overs: editUser.overs || "",
        reportingTime: editUser.reportingTime || "",
        groundLocation: editUser.location || "",
        opponents: editUser.opponents || "",
        matchFee: editUser.fees || "",
        remarks: editUser.remarks || "",
      });

      setSelectedMatchType(
        editUser.matchType
          ? { value: editUser.matchType, label: editUser.matchType }
          : null,
      );

      setSelectedBallType(
        editUser.ballType
          ? { value: editUser.ballType, label: editUser.ballType }
          : null,
      );
    }
  }, [isEditMode, editUser]);

  const handleChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));

    setErrors((prev) => ({
      ...prev,
      [e.target.name]: "",
    }));
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.name.trim()) newErrors.name = "Name is required";
    if (!formData.startDate) newErrors.startDate = "Start Date is required";
    if (!formData.endDate) newErrors.endDate = "End Date is required";
    if (!formData.overs) newErrors.overs = "Overs is required";
    if (!selectedBallType)
      newErrors.ballType = "Ball Type selection is required";
    if (!selectedMatchType)
      newErrors.matchType = "Match Type selection is required";
    if (!formData.matchFee) newErrors.matchFee = "Match Fee is required";
    if (!formData.reportingTime)
      newErrors.reportingTime = "Reporting Time is required";
    if (!formData.groundLocation.trim())
      newErrors.groundLocation = "Ground Location is required";
    if (!formData.remarks.trim()) newErrors.remarks = "Remarks are required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setLoading(true);

    const payload = {
      ...(isEditMode ? { id: editUser.id } : {}),
      name: formData.name,
      location: formData.groundLocation,
      startDate: formData.startDate
        ? formatDate(formData.startDate)
        : null,
      endDate: formData.endDate
        ? formatDate(formData.endDate)
        : null,
      overs: Number(formData.overs),
      matchType: selectedMatchType?.value,
      ballType: selectedBallType?.value,
      reportingTime: formData.reportingTime + ":00",
      fees: Number(formData.matchFee),
      remarks: formData.remarks,
    };

    try {
      const res = await createAndUpdateTournaments(payload);

      toast.success(
        isEditMode
          ? "Tournament updated successfully!"
          : "Tournament created successfully!",
        { autoClose: 1000 },
      );

      navigate("/admin/all-tournament");
    } catch (err) {
      console.error("Error:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    if (isEditMode && editUser) {
      setFormData({
        name: editUser.name || "",
        matchDate: editUser.startDate ? new Date(editUser.startDate) : null,
        overs: editUser.overs || "",
        reportingTime: editUser.reportingTime || "",
        groundLocation: editUser.location || "",
        opponents: editUser.opponents || "",
        matchFee: editUser.fees || "",
        remarks: editUser.remarks || "",
      });
    } else {
      setFormData({
        name: "",
        matchDate: null,
        overs: "",
        reportingTime: "",
        groundLocation: "",
        opponents: "",
        matchFee: "",
        remarks: "",
      });
    }
  };

  useEffect(() => {
    getMatchType().then(setMatchType);
    getBallType().then(setBallType);
  }, []);

  const handleDateChange = (date, field) => {
    setFormData((prev) => ({ ...prev, [field]: date }));
  };

  return (
    <>
      <div className="page-titles d-flex justify-content-between align-items-center">
        <h4 className="mb-0">{isEditMode ? "Edit" : "Add"} Tournament</h4>
        <ol className="breadcrumb mb-0">
          <li className="breadcrumb-item ">
            <Link to="/admin/all-tournament">Tournaments</Link>
          </li>
          <li className="breadcrumb-item active">
            <Link to={"#"}>
              {isEditMode ? "Edit Tournament" : "Add Tournament"}
            </Link>
          </li>
        </ol>
      </div>
      <div className="row">
        <div className="col-xl-12 col-xxl-12 col-sm-12">
          <div className="card">
            <div className="card-header">
              <h5 className="card-title">
                {isEditMode ? "Edit" : "Add"} Tournament
              </h5>
            </div>
            <div className="card-body">
              <form>
                <div className="row">
                  {/* Name of the Student */}
                  <div className="col-sm-6">
                    <div className="form-group">
                      <label className="form-label" htmlFor="name">
                        Tournament / Practice Name
                        <span className="text-danger">*</span>
                      </label>
                      <input
                        id="name"
                        name="name"
                        placeholder="Enter Full Name"
                        type="text"
                        className={`form-control ${
                          errors.name ? "is-invalid" : ""
                        }`}
                        value={formData.name}
                        onChange={handleChange}
                      />
                      {errors.name && (
                        <div className="invalid-feedback">{errors.name}</div>
                      )}
                    </div>
                  </div>

                  <div className="col-sm-6">
                    <div className="form-group">
                      <label className="form-label" htmlFor="startDate">
                        Start Date <span className="text-danger">*</span>
                      </label>
                      <div className="input-hasicon mb-xl-0 mb-3">
                        <DatePicker
                          placeholder="Select Start Date"
                          className="picker-suit"
                          value={formData.startDate}
                          onChange={(date) => {
                            handleDateChange(date, "startDate");
                            setErrors((prev) => ({
                              ...prev,
                              startDate: "",
                            }));
                          }}
                        />
                        <div className="icon">
                          <i className="far fa-calendar" />
                        </div>
                      </div>
                      {errors.startDate && (
                        <div className="text-danger mt-1">
                          {errors.startDate}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="col-sm-6">
                    <div className="form-group">
                      <label className="form-label" htmlFor="endDate">
                        End Date <span className="text-danger">*</span>
                      </label>
                      <div className="input-hasicon mb-xl-0 mb-3">
                        <DatePicker
                          placeholder="Select End Date"
                          className="picker-suit"
                          value={formData.endDate}
                          onChange={(date) => {
                            handleDateChange(date, "endDate");
                            setErrors((prev) => ({
                              ...prev,
                              endDate: "",
                            }));
                          }}
                        />
                        <div className="icon">
                          <i className="far fa-calendar" />
                        </div>
                      </div>
                      {errors.endDate && (
                        <div className="text-danger mt-1">{errors.endDate}</div>
                      )}
                    </div>
                  </div>

                  <div className="col-sm-6">
                    <div className="form-group">
                      <label className="form-label" htmlFor="overs">
                        Overs <span className="text-danger">*</span>
                      </label>
                      <input
                        id="overs"
                        placeholder="Enter Overs"
                        type="number"
                        className={`form-control ${
                          errors.overs ? "is-invalid" : ""
                        }`}
                        name="overs"
                        value={formData.overs}
                        onChange={handleChange}
                      />
                      {errors.overs && (
                        <div className="invalid-feedback">{errors.overs}</div>
                      )}
                    </div>
                  </div>

                  <div className="col-sm-6">
                    <div className="form-group">
                      <label className="form-label">Ball Type</label>
                      <Select
                        isSearchable={false}
                        options={ballType?.map((type) => ({
                          value: type.key,
                          label: type.value,
                        }))}
                        placeholder="Select Ball Type"
                        className="custom-react-select"
                        value={selectedBallType}
                        onChange={(selectedOption) => {
                          setSelectedBallType(selectedOption);
                          setErrors((prev) => ({
                            ...prev,
                            ballType: "",
                          }));
                        }}
                        styles={{
                          control: (base) => ({
                            ...base,
                            borderColor: errors.ballType
                              ? "#FF1616"
                              : base.borderColor,
                            boxShadow: errors.ballType
                              ? "0 0 0 1px #FF1616"
                              : base.boxShadow,
                            "&:hover": {
                              borderColor: errors.ballType
                                ? "#FF1616"
                                : base.borderColor,
                            },
                          }),
                        }}
                      />
                      {errors.ballType && (
                        <div className="invalid-feedback d-block">
                          {errors.ballType}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Reporting Time */}
                  <div className="col-sm-6">
                    <div className="form-group">
                      <label className="form-label" htmlFor="reportingTime">
                        Reporting Time
                      </label>
                      <input
                        id="reportingTime"
                        placeholder="e.g. 07:30 AM"
                        type="time"
                        className={`form-control ${
                          errors.reportingTime ? "is-invalid" : ""
                        }`}
                        name="reportingTime"
                        value={formData.reportingTime}
                        onChange={handleChange}
                      />
                      {errors.reportingTime && (
                        <div className="invalid-feedback">
                          {errors.reportingTime}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Ground Location */}
                  <div className="col-sm-6">
                    <div className="form-group">
                      <label className="form-label" htmlFor="groundLocation">
                        Ground Location
                      </label>
                      <input
                        id="groundLocation"
                        name="groundLocation"
                        placeholder="Enter Ground Name or Location"
                        type="text"
                        className={`form-control ${
                          errors.groundLocation ? "is-invalid" : ""
                        }`}
                        value={formData.groundLocation}
                        onChange={handleChange}
                      />
                      {errors.groundLocation && (
                        <div className="invalid-feedback">
                          {errors.groundLocation}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Match Type */}
                  <div className="col-sm-6">
                    <div className="form-group">
                      <label className="form-label">Match Type</label>
                      <Select
                        isSearchable={false}
                        options={matchType?.map((type) => ({
                          value: type.key,
                          label: type.value,
                        }))}
                        placeholder="Select Match Type"
                        className="custom-react-select"
                        value={selectedMatchType}
                        onChange={(selectedOption) => {
                          setSelectedMatchType(selectedOption);
                          setErrors((prev) => ({
                            ...prev,
                            matchType: "",
                          }));
                        }}
                        styles={{
                          control: (base) => ({
                            ...base,
                            borderColor: errors.matchType
                              ? "#FF1616"
                              : base.borderColor,
                            boxShadow: errors.matchType
                              ? "0 0 0 1px #FF1616"
                              : base.boxShadow,
                            "&:hover": {
                              borderColor: errors.matchType
                                ? "#FF1616"
                                : base.borderColor,
                            },
                          }),
                        }}
                      />
                      {errors.matchType && (
                        <div className="invalid-feedback d-block">
                          {errors.matchType}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="col-sm-6">
                    <div className="form-group">
                      <label className="form-label" htmlFor="matchFee">
                        Match Fee <span className="text-danger">*</span>
                      </label>
                      <input
                        id="matchFee"
                        name="matchFee"
                        placeholder="Enter Match Fee"
                        type="number"
                        className={`form-control ${
                          errors.matchFee ? "is-invalid" : ""
                        }`}
                        value={formData.matchFee}
                        onChange={handleChange}
                      />
                      {errors.matchFee && (
                        <div className="invalid-feedback">
                          {errors.matchFee}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* <div className="col-sm-6">
                    <div className="form-group">
                      <label className="form-label">Status</label>
                      <Select
                        isSearchable={false}
                        options={matchType?.map((type) => ({
                          value: type.key,
                          label: type.value,
                        }))}
                        placeholder="Select Status"
                        className="custom-react-select"
                        value={selectedMatchType}
                        onChange={(selectedOption) => {
                          setSelectedMatchType(selectedOption);
                          setErrors((prev) => ({
                            ...prev,
                            matchType: "",
                          }));
                        }}
                        styles={{
                          control: (base) => ({
                            ...base,
                            borderColor: errors.matchType
                              ? "#FF1616"
                              : base.borderColor,
                            boxShadow: errors.matchType
                              ? "0 0 0 1px #FF1616"
                              : base.boxShadow,
                            "&:hover": {
                              borderColor: errors.matchType
                                ? "#FF1616"
                                : base.borderColor,
                            },
                          }),
                        }}
                      />
                      {errors.matchType && (
                        <div className="invalid-feedback d-block">
                          {errors.matchType}
                        </div>
                      )}
                    </div>
                  </div> */}

                  {/* Remarks */}
                  <div className="col-lg-12 col-md-12 col-sm-12">
                    <div className="form-group">
                      <label className="form-label" htmlFor="remarks">
                        Remarks / Notes <span className="text-danger">*</span>
                      </label>
                      <textarea
                        id="remarks"
                        name="remarks"
                        placeholder="Add any notes about the match"
                        className={`form-control ${
                          errors.remarks ? "is-invalid" : ""
                        }`}
                        rows="3"
                        value={formData.remarks}
                        onChange={handleChange}
                      />
                      {errors.remarks && (
                        <div className="invalid-feedback">{errors.remarks}</div>
                      )}
                    </div>
                  </div>

                  {/* Submit & Cancel */}
                  <div className="col-lg-12 col-md-12 col-sm-12">
                    <button
                      type="button"
                      onClick={handleSubmit}
                      className="btn btn-primary me-1"
                      disabled={loading}
                    >
                      {loading ? "Submitting..." : "Submit"}
                    </button>
                    <Link
                      to="/admin/all-tournament"
                      className="btn btn-danger light"
                      onClick={handleReset}
                    >
                      Cancel
                    </Link>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AddTournaments;
